import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { CoreProvider } from './core.provider';
import { EventDto } from './local-data/games.service';

declare var cordova: { plugins: { permissions: any } };

interface HAIBenchNativeInterface {
  handleRequest: (method: string, uri: string, body: string) => Promise<object|object[]>;
  getWifiIpAddress: (success: (result: { ipAddress: string }) => void, error: (error: string) => void) => void;
  startServer: (success: (result: string) => void, error: (error: string) => void) => void;
  stopServer: (success: (result: string) => void, error: (error: string) => void) => void;
  isServerRunning: (success: (result: { running: boolean; port?: number }) => void, error: (error: string) => void) => void;
  setResponse: (requestKey: string, response: string) => void;
}

declare var HAIBenchNative: HAIBenchNativeInterface;

@Injectable({
  providedIn: 'root'
})
export class NativeService {
  private core?: CoreProvider;
  private lastRecording?: string;

  constructor(private platform: Platform) {
  }

  setLastRecording(lastRecording: string) {
    this.lastRecording = lastRecording;
  }

  /**
   * The native local server (HAIBenchNative plugin) is available on Android and iOS native apps
   */
  private get isNativeServerPlatform(): boolean {
    return this.platform.is('android') || this.platform.is('ios');
  }

  async initCore(core: CoreProvider) {
    this.core = core;

    await this.core.platform.ready()
    console.log('HAIBenchNative available:', 'HAIBenchNative' in window);
    if ('HAIBenchNative' in window) {
      // Connect request handler to native plugin
      HAIBenchNative.handleRequest = async (method: string, uri: string, body: string) => await this.handleRequest(method, uri, body);
    }
  }

  // Method that will be called from native code
  async handleRequest(method: string, uri: string, body: string): Promise<object|object[]> {
    const bodyParsed = body ? JSON.parse(body) : null;
    console.log('Incoming native request:', method, uri, body);

    if (uri.startsWith('/game-info/')) {
      if (!this.core!.gameTrackerService.gameDto) return {"result": false};
      const gameId = uri.split('/')[2];
      if (this.core!.gameTrackerService.gameDto.game.localId !== gameId) return {"result": false};

      this.core!.gameTrackerService.firstGameInfoRequest$.next(true);
      return { result: true, game: this.core!.gameTrackerService.gameDto };

    } else if (uri.startsWith('/game-events/')) {
      if (!this.core!.gameTrackerService.gameDto) return {"result": false};
      const gameId = uri.split('/')[2];
      if (this.core!.gameTrackerService.gameDto.game.localId !== gameId) return {"result": false};
      const sinceOrderId = uri.split('/')[3];

      const events = this.core?.gameTrackerService.gameEvents$.getValue().filter(event => event.orderId > +sinceOrderId);

      return { result: true, events: events || [], modified: this.core!.gameTrackerService.gameDto.modified };
    } else if (uri.startsWith('/update-event/') && method == 'POST') {
      if (!this.core!.gameTrackerService.gameDto) return {"result": false};
      const gameId = uri.split('/')[2];
      if (this.core!.gameTrackerService.gameDto.game.localId !== gameId) return {"result": false};
      await this.core!.gameTrackerService.updateEvent(bodyParsed as EventDto);
      return {"result": true};
    } else if (uri.startsWith('/delete-event/') && method == 'DELETE') {
      if (!this.core!.gameTrackerService.gameDto) return {"result": false};
      const gameId = uri.split('/')[2];
      if (this.core!.gameTrackerService.gameDto.game.localId !== gameId) return {"result": false};
      const eventUuid = uri.split('/')[3];
      await this.core!.gameTrackerService.deleteEvent(eventUuid);
      return {"result": true};
    } else if (uri == '/last-recording') {
      if (!this.lastRecording) return {result: false, detail: 'No recording found'};
      if (!this.core!.gameTrackerService.cameraDeviceUrl) return {result: false, detail: 'Camera device URL not set'};

      return {"result": true, url: `${this.core!.gameTrackerService.cameraDeviceUrl}/static/${this.lastRecording}`};
    } else {
      return {"result": false, detail: "Unknown request", method, uri, body};
    }
  }

  async getWifiIpAddress(): Promise<{ ipAddress: string }> {
    if (!('HAIBenchNative' in window)) throw new Error('HAIBenchNative plugin not properly loaded, maybe not running as native app');
    if (this.isNativeServerPlatform) {
      return new Promise((resolve, reject) => {
        HAIBenchNative.getWifiIpAddress(
          (success: any) => resolve(success),
          (error: string) => reject(error)
        );
      });
    } else {
      throw new Error('This plugin is only available on Android and iOS');
    }
  }

  async startServer(): Promise<string> {
    if (this.isNativeServerPlatform) {
      console.log('Starting server...');
      console.log('HAIBenchNative available:', 'HAIBenchNative' in window);
      // console.log('HAIBenchNative methods:', Object.keys('HAIBenchNative' in window));

      return new Promise((resolve, reject) => {
        if (!HAIBenchNative || !HAIBenchNative.startServer) {
          reject(new Error('HAIBenchNative plugin not properly loaded'));
          return;
        }

        HAIBenchNative.startServer(
          (success: string) => {
            console.log('Server started successfully:', success);
            resolve(success);
          },
          (error: string) => {
            console.error('Failed to start server:', error);
            reject(error);
          }
        );
      });
    } else {
      throw new Error('This plugin is only available on Android and iOS');
    }
  }

  async stopServer(): Promise<string> {
    if (!('HAIBenchNative' in window)) throw new Error('HAIBenchNative plugin not properly loaded, maybe not running as native app');
    if (this.isNativeServerPlatform) {
      return new Promise((resolve, reject) => {
        HAIBenchNative.stopServer(
          (success: string) => resolve(success),
          (error: string) => reject(error)
        );
      });
    } else {
      throw new Error('This plugin is only available on Android and iOS');
    }
  }

  async isServerRunning(): Promise<{ running: boolean; port?: number }> {
    if (!('HAIBenchNative' in window)) throw new Error('HAIBenchNative plugin not properly loaded, maybe not running as native app');
    if (this.isNativeServerPlatform) {
      return new Promise((resolve, reject) => {
        HAIBenchNative.isServerRunning(
          (success: any) => resolve(success),
          (error: string) => resolve({running: false, port: undefined})
        );
      });
    } else {
      throw new Error('This plugin is only available on Android and iOS');
    }
  }

  async requestPermissions() {
    if (typeof cordova !== 'undefined' && cordova.plugins && cordova.plugins.permissions) {
      try {
        const permissions = [
          cordova.plugins.permissions.CAMERA,
          cordova.plugins.permissions.RECORD_AUDIO
        ];

        for (const permission of permissions) {
          const result = await new Promise((resolve, reject) => {
            cordova.plugins.permissions.checkPermission(permission, (status: any) => {
              if (!status.hasPermission) {
                cordova.plugins.permissions.requestPermission(permission, (status: any) => {
                  resolve(status.hasPermission);
                }, (error: any) => {
                  reject(error);
                });
              } else {
                resolve(true);
              }
            }, (error: any) => {
              reject(error);
            });
          });
          console.log(`Permission ${permission} result:`, result);
        }
      } catch (error) {
        console.error('Error requesting permissions:', error);
      }
    }
  }

  /**
   * Setup the game tracker server and generate the QR code data
   * @returns the QR code data
   */
  async setupGameTrackerServer() {
    if (this.isNativeServerPlatform && 'HAIBenchNative' in window) {
      // Start server if not already running
      let serverRunning = await this.isServerRunning();
      if (!serverRunning.running) {
        await this.startServer();
        serverRunning = await this.isServerRunning();
      }

      // Generate QR code data
      const ip = await this.getWifiIpAddress();
      this.core!.gameTrackerService.cameraDeviceUrl = `https://${ip.ipAddress}:${serverRunning.port}`;
      return `haibench://${this.core!.auth.user!.sub}@${ip.ipAddress}:${serverRunning.port}/${this.core!.gameTrackerService.gameDto!.game.localId}/${this.core!.gameTrackerService.gameDto!.game.accessHash}`;
    } else {
      return `haibench://${this.core!.auth.user!.sub}@/${this.core!.gameTrackerService.gameDto!.game.localId}/${this.core!.gameTrackerService.gameDto!.game.accessHash}`;
    }
  }

}